import {Injectable, Injector} from '@angular/core';

import {firstValueFrom, Subscription} from 'rxjs';
import {LanguageService} from "../../../core/i18n/language.service";
import {DiscountService} from "../../../apps/subscriptions/shared/service/discount.service";
import {LoggingService} from "../../../core/service/logging.service";
import {ProductCacheService} from "../../service/product-cache.service";
import {EventService} from "../../../core/service/event.service";
import {AuthenticationService} from "../../../core/service/auth.service";
import {CurrencyCode, DiscountState, PricingInfo} from "./upgrade-modal.model";
import {currentSubscriptionLevel, featureVariantFor, UpgradeModalRequest} from '../../limits/limit-failure.model';
import {UpgradeModalComponent} from "./upgrade-modal.component";
import {HttpErrorResponse} from "@angular/common/http";
import {PendingDiscountDto} from "../../../apps/subscriptions/shared/model/discount/PendingDiscountDto";
import {ModalRef, ModalService} from "../../modal";
import {DiscountTerms, presentDiscount} from "../../../apps/subscriptions/shared/model/discount/discount-pricing";



@Injectable({
    providedIn: 'root'
})
export class UpgradeModalService {

    private readonly COUPON_CODE = '1STTIME50OFF';
    private subscription: Subscription = new Subscription();
    private _languageService: LanguageService | null = null;
    private isUpgradeModalOpen = false;
    private initialized = false;

    constructor(
        private readonly modalService: ModalService,
        private readonly discountService: DiscountService,
        private readonly logger: LoggingService,
        private readonly productCacheService: ProductCacheService,
        private readonly eventService: EventService,
        private readonly authService: AuthenticationService,
        private readonly injector: Injector
    ) {
    }

    /**
     * Wires up the upgrade-modal event listener and the subscription-change preload.
     * Called from LayoutContainerComponent.ngOnInit() — deliberately NOT from the constructor:
     * getSubscriptionChanges() is a BehaviorSubject, so in the constructor it would replay the
     * already-loaded subscriptions synchronously → loadProducts() → http.get('/products') while
     * the HTTP_INTERCEPTORS chain is still being built → NG0200 (circular DI). Running it from
     * ngOnInit (after construction + interceptor build) avoids that. This service is also injected
     * nowhere else, so this is the single point where it is created and starts listening.
     */
    init(): void {
        if (this.initialized) {
            return;
        }
        this.initialized = true;

        this.subscription.add(
            this.eventService.subscribe('show-upgrade-modal', (payload: UpgradeModalRequest) => {
                void this.openOnce(payload);
            })
        );

        this.subscription.add(
            this.authService.getSubscriptionChanges().subscribe((subscriptions) => {
                if (subscriptions?.includes('guideglare_zero')) {
                    this.productCacheService.loadProducts();
                }
            })
        );
    }

    /**
     * Opens the upgrade modal unless one is already open. Single-flight guard moved here from
     * WSErrorsService so every entry point (HTTP 418, WS limit errors, in-app CTAs) dedupes.
     */
    private async openOnce(payload?: UpgradeModalRequest): Promise<void> {
        try {
            await this.open(payload);
        } catch (error) {
            this.logger.error('Failed to open upgrade modal', error);
        }
    }

    /**
     * Gets the preferred currency from user's language settings (lazy injection to avoid cycles)
     */
    private getPreferredCurrency(): CurrencyCode {
        if (!this._languageService) {
            this._languageService = this.injector.get(LanguageService);
        }
        return (this._languageService.getCurrentLanguageSnapshot().preferredCurrency as CurrencyCode) || 'EUR';
    }

    /**
     * Opens the upgrade modal with pre-loaded discount and pricing data.
     * @param options - Modal options
     */
    async open(options: UpgradeModalRequest = {}): Promise<ModalRef | null> {
        if (this.isUpgradeModalOpen) return null;
        this.isUpgradeModalOpen = true;
        let modalRef: ModalRef | null = null;
        let planWatch: Subscription | undefined;
        try {
            const subscriptions = await this.authService.getCurrentUserActiveSubscriptions();
            const level = currentSubscriptionLevel(subscriptions.map(sub => sub.subscription));
            if (!level || level === 'top') return null;
            // A stale FREE teaser can stay mounted through checkout; it no longer offers anything.
            if (level !== 'zero' && options.featureLimitVariant !== undefined && !options.limit) return null;
            let invalidated = false;
            planWatch = this.authService.getSubscriptionChanges().subscribe(subs => {
                if (currentSubscriptionLevel(subs) !== level) {
                    invalidated = true;
                    modalRef?.dismiss('subscription-changed');
                }
            });
            const discountState: DiscountState = level === 'zero' ? await this.loadDiscountState()
                : {available: false, pending: false, discount: null, pendingDiscount: null};
            const currency = level === 'zero' ? this.getPreferredCurrency() : 'EUR';
            const [pricingInfo, advancedPricingInfo] = level === 'zero' ? await Promise.all([
                this.loadPricingInfo(discountState, currency),
                this.loadPricingInfo(discountState, currency, 'advanced'),
            ]) : [null, null];
            if (invalidated) return null;
            const featureLimitVariant = level === 'zero' ? featureVariantFor(options.limit) ?? options.featureLimitVariant : undefined;
            modalRef = this.modalService.open(UpgradeModalComponent, {
                centered: true, size: 'md', backdrop: 'static', keyboard: true,
                inputs: {subscriptionLevel: level, discountState, pricingInfo, advancedPricingInfo, featureLimitVariant,
                    source: options.source ?? 'feature', limit: options.limit,
                    chatId: options.chatId, interruptionId: options.interruptionId},
            });
            const release = () => {
                planWatch?.unsubscribe();
                this.isUpgradeModalOpen = false;
            };
            void modalRef.result.then(release, release);
            return modalRef;
        } finally {
            if (!modalRef) {
                planWatch?.unsubscribe();
                this.isUpgradeModalOpen = false;
            }
        }
    }

    private async loadDiscountState(): Promise<DiscountState> {
        const state: DiscountState = {
            available: false,
            pending: false,
            discount: null,
            pendingDiscount: null
        };

        try {
            const pending = await this.checkPendingDiscount();
            if (pending) {
                state.pending = true;
                state.pendingDiscount = pending;
                return state;
            }

            const response = await this.discountService.checkCouponAvailability(this.COUPON_CODE);
            if (response && response.available) {
                state.available = true;
                state.discount = response.discount;
            }
        } catch (error) {
            this.logger.error('Error loading discount state', error as HttpErrorResponse);
        }

        return state;
    }

    private async loadPricingInfo(discountState: DiscountState, currency: CurrencyCode,
                                  plan: 'basic' | 'advanced' = 'basic'): Promise<PricingInfo | null> {
        try {
            const product = plan === 'advanced' ? await this.productCacheService.getProductByPlanType(plan) : null;
            const productPrice = plan === 'basic' ? await this.productCacheService.getBasicProductPrice(currency)
                : product?.available ? this.productCacheService.getProductPrice(product, currency) : null;
            if (!productPrice) {
                return null;
            }

            // The API sends the price the customer pays first (already reduced by a pending
            // coupon) together with the price before the discount — nothing is reconstructed here.
            const originalPrice = productPrice.originalPrice ?? productPrice.price;
            let discountedPrice = productPrice.price;
            let terms: DiscountTerms | null = null;

            const activeDiscount = discountState.pendingDiscount || discountState.discount;
            if (productPrice.appliedDiscount || discountedPrice < originalPrice) {
                terms = discountState.pendingDiscount ?? productPrice.appliedDiscount ?? null;
            } else if (activeDiscount && (discountState.pending || discountState.available)) {
                // A coupon that is only offered, not yet applied: the API price is still the full one.
                discountedPrice = this.productCacheService.calculateDiscountedPrice(
                    originalPrice,
                    activeDiscount.percentOff,
                    activeDiscount.amountOff
                );
                terms = activeDiscount;
            }
            const hasDiscount = discountedPrice < originalPrice;

            return {
                originalPrice,
                discountedPrice,
                currency,
                formattedOriginalPrice: this.productCacheService.formatPrice(originalPrice, currency),
                formattedDiscountedPrice: this.productCacheService.formatPrice(discountedPrice, currency),
                hasDiscount,
                presentation: hasDiscount ? presentDiscount(terms) : null,
            };
        } catch (error) {
            this.logger.error('Error loading pricing info', error as HttpErrorResponse);
            return null;
        }
    }

    private async checkPendingDiscount(): Promise<PendingDiscountDto | null> {
        try {
            return await firstValueFrom(this.discountService.getPendingDiscount());
        } catch {
            return null;
        }
    }
}
